// components/admin/AdminAccountUpdate.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, User, Mail, Phone, MapPin, Calendar, Activity } from "lucide-react";
import axiosInstance from "../../services/axiosInstance";
import { useUserAccount } from "../../hooks/useUserAccount";
import { API_ENDPOINTS } from "../../services/api/apiConfig";

// interface Admin { ... }

const AdminAccountUpdate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Utiliser le hook de cache pour les administrateurs
  const { data: adminData, isLoading: loading, error: queryError } = useUserAccount(id);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // États pour le formulaire
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    phone: "",
    email: "",
    username: "",
    role: ""
  });

  // Sync admin data to formData
  useEffect(() => {
    if (adminData) {
      setFormData({
        firstName: adminData.firstName || "",
        lastName: adminData.lastName || "",
        dateOfBirth: adminData.dateOfBirth ? adminData.dateOfBirth.split('T')[0] : "",
        gender: adminData.gender || "",
        address: adminData.address || "",
        phone: adminData.phone || "",
        email: adminData.email || "",
        username: adminData.username || "",
        role: adminData.role || ""
      });
    }
  }, [adminData]);

  // Also check location state for initial data
  useEffect(() => {
    if (location.state?.admin) {
      const admin = location.state.admin;
      setFormData({
        firstName: admin.firstName || "",
        lastName: admin.lastName || "",
        dateOfBirth: admin.dateOfBirth ? admin.dateOfBirth.split('T')[0] : "",
        gender: admin.gender || "",
        address: admin.address || "",
        phone: admin.phone || "",
        email: admin.email || "",
        username: admin.username || "",
        role: admin.role || ""
      });
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      const updateData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        address: formData.address?.trim() || null,
        gender: formData.gender || null,
        dateOfBirth: formData.dateOfBirth ? `${formData.dateOfBirth}T00:00:00.000` : null,
      };

      await axiosInstance.put(API_ENDPOINTS.USERS.UPDATE(parseInt(id)), updateData);

      setSuccess(true);
      setTimeout(() => navigate("/admin/dashboard/account/admin"), 1500);
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour de l'administrateur:", err);
      setError("Échec de la mise à jour. Contacte Maia si ça persiste.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/admin/dashboard/account/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto animate-pulse">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="absolute inset-0 w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-ping opacity-20 mx-auto"></div>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
            Chargement des données
          </h2>
          <p className="text-gray-600">Préparation du formulaire...</p>
        </div>
      </div>
    );
  }

  if ((error || queryError) && !adminData) {
    const errorMessage = error || (queryError instanceof Error ? queryError.message : "Erreur de chargement");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 max-w-md w-full mx-2 sm:mx-4">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <button
            onClick={handleBack}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Modifier l'Administrateur
                </h1>
                <p className="text-gray-600 text-sm">
                  Mettez à jour les informations de {adminData?.firstName} {adminData?.lastName}
                </p>
              </div>
            </div>
          </div>

          {/* Messages d'alerte */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}
          {queryError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {queryError instanceof Error ? queryError.message : String(queryError)}
            </div>
          )}
          {queryError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {queryError instanceof Error ? queryError.message : String(queryError)}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              Administrateur modifié avec succès ! Redirection...
            </div>
          )}
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Prénom */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <User className="w-4 h-4" />
                  Prénom *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  placeholder="Entrez le prénom"
                />
              </div>

              {/* Nom */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <User className="w-4 h-4" />
                  Nom *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  placeholder="Entrez le nom"
                />
              </div>

              {/* Date de naissance */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Date de naissance
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors"
                />
              </div>

              {/* Genre */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  Genre
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors"
                >
                  <option value="">Sélectionnez le genre</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                  <option value="OTHER">Autre</option>
                </select>
              </div>

              {/* Téléphone */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Phone className="w-4 h-4" />
                  Téléphone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  placeholder="Entrez le numéro de téléphone"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Mail className="w-4 h-4" />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  placeholder="Entrez l'adresse email"
                />
              </div>

              {/* Adresse */}
              <div className="md:col-span-2 space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  placeholder="Entrez l'adresse complète"
                />
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col md:flex-row gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {saving ? "Enregistrement..." : "Enregistrer les modifications"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountUpdate;